/**
 * Список дел (ТЗ §8.4): серверная пагинация `GET /api/cases?page&limit`, фильтры
 * (стадия, категория, приоритет, исполнитель, отделение, источник, срок), поиск и чипы.
 * Всё состояние фильтров живёт в query string (`useCaseListParams`): ссылка на отфильтрованный список открывается тем же
 * списком, а «назад» и «вперёд» проходят по прежним выборкам.
 *
 * Маршруты: `/cases` и `/cases/:id` ведут на этот же экран; карточку дела поверх него
 * рисует `App` (`CaseCardModal` на совпадение пути), поэтому прямая ссылка `/cases/:id`
 * открывает карточку, а под ней — отфильтрованный список. Клик по строке — `useOpenCase`.
 */
import { useMemo } from 'react';

import { useAuth } from '@/auth/AuthContext';
import { useOpenCase } from '@/components/Modal';
import { useSettingsDepartments, useSettingsUsers } from '@/api/hooks';
import { caseWord } from '@/lib/format';
import { CASES_CHIPS, CHIP_LABELS, CaseFilters } from '@/components/cases/CaseFilters';
import type { CaseSort } from '@/components/cases/CasesTable';
import { CasesTable, useCaseList } from '@/components/cases/CasesTable';
import { useCaseListParams } from '@/components/cases/useCaseListParams';
import { Pagination } from '@/components/cases/Pagination';

const SORT_LABELS: Readonly<Record<CaseSort, string>> = {
    deadline: 'по сроку ответа',
    created: 'по поступлению',
    updated: 'по активности',
    priority: 'по приоритету',
};

const SORT_ORDER: readonly CaseSort[] = ['deadline', 'created', 'updated', 'priority'];

export function CasesPage() {
    const openCase = useOpenCase();
    const { user, role, isChief } = useAuth();
    const { filters, page, limit, sort, changeFilters, changeParam } = useCaseListParams(CASES_CHIPS);

    // Справочники для селектов есть только у главврача: `/api/settings/*` закрыты ролью.
    const users = useSettingsUsers({ enabled: isChief });
    const departments = useSettingsDepartments({ enabled: isChief });

    const executorOptions = useMemo(() => {
        const items = users.data?.items ?? [];
        return items
            .filter((item) => item.is_active)
            .slice()
            .sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
    }, [users.data]);

    const query = useMemo(() => ({ ...filters, page, limit, sort }), [filters, page, limit, sort]);
    const cases = useCaseList(query);

    /* Чип «Повторные» приходит выборкой `kpi=repeat`, а у неё в предикате «дело открыто»:
       со стадией «Ответ направлен» такая пара пуста всегда, и молчать об этом нельзя. */
    const emptyText =
        filters.chip === 'repeat' && filters.stage === 'sent'
            ? 'Повторные дела показываются только среди открытых: снимите стадию «Ответ направлен» или выберите чип «Закрытые».'
            : undefined;

    return (
        <>
            <CaseFilters
                value={filters}
                onChange={changeFilters}
                chips={CASES_CHIPS}
                showSelects
                executors={executorOptions}
                departments={departments.data?.items ?? []}
                role={role ?? undefined}
                meId={user?.id}
            />

            <div className="stat-block">
                <div className="block-header">
                    <span>
                        🗂 Дела{' '}
                        <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f' }}>
                            · {CHIP_LABELS[filters.chip].toLowerCase()}
                            {cases.total > 0 ? `, ${cases.total} ${caseWord(cases.total)}` : ''}
                        </span>
                    </span>
                    <span style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f', fontSize: 14 }}>
                            Сортировка
                        </span>
                        <select
                            className="act-select"
                            aria-label="Сортировка"
                            value={sort}
                            onChange={(event) => changeParam('sort', event.target.value)}
                        >
                            {SORT_ORDER.map((option) => (
                                <option key={option} value={option}>
                                    {SORT_LABELS[option]}
                                </option>
                            ))}
                        </select>
                    </span>
                </div>
                <div className="block-body" id="casesBlock" aria-busy={cases.isFetching}>
                    <CasesTable
                        items={cases.items}
                        variant="full"
                        onOpen={openCase}
                        loading={cases.isPending}
                        error={cases.error}
                        emptyText={emptyText}
                    />
                    {cases.truncated ? (
                        <div className="case-more">
                            Выборка «Повторные» приходит с сервера не более чем 200 делами: всего таких
                            дел {cases.serverTotal}. Уточните фильтры или поиск, чтобы увидеть остальные.
                        </div>
                    ) : null}
                </div>
                <Pagination
                    page={cases.page}
                    limit={cases.limit}
                    total={cases.total}
                    pages={cases.pages}
                    onPage={(next) => changeParam('page', next)}
                    onLimit={(next) => changeParam('limit', next)}
                />
            </div>
        </>
    );
}

export default CasesPage;
